import React, { useState, useEffect } from 'react';
import { ToastProvider } from './context/ToastContext';
import { StoreProvider } from './context/StoreContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

// Storefront components
import { AnnouncementBar } from './components/layout/AnnouncementBar';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { CartDrawer } from './components/cart/CartDrawer';

// Storefront pages
import { HomePage } from './pages/HomePage';
import { CatalogPage } from './pages/CatalogPage';
import { CategoryPage } from './pages/CategoryPage';
import { ProductPage } from './pages/ProductPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { AccountPage } from './pages/AccountPage';
import { LoginPage } from './pages/LoginPage';
import { PolicyPage } from './pages/PolicyPage';

// Admin layout & pages
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminProducts } from './pages/admin/AdminProducts';
import { AdminCategories } from './pages/admin/AdminCategories';
import { AdminOrders } from './pages/admin/AdminOrders';
import { AdminSettings } from './pages/admin/AdminSettings';

function AppContent() {
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname || '/');

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    if (path !== currentPath) {
      window.history.pushState({}, '', path);
      setCurrentPath(path.split('?')[0]);
      window.scrollTo(0, 0);
    }
  };

  // Route matching helper
  const renderRoute = () => {
    const path = currentPath;

    // Admin Routes
    if (path === '/admin') {
      return (
        <AdminLayout currentPath={path} onNavigate={navigate}>
          <AdminDashboard onNavigate={navigate} />
        </AdminLayout>
      );
    }
    if (path === '/admin/produtos') {
      return (
        <AdminLayout currentPath={path} onNavigate={navigate}>
          <AdminProducts onNavigate={navigate} />
        </AdminLayout>
      );
    }
    if (path === '/admin/categorias') {
      return (
        <AdminLayout currentPath={path} onNavigate={navigate}>
          <AdminCategories onNavigate={navigate} />
        </AdminLayout>
      );
    }
    if (path === '/admin/pedidos') {
      return (
        <AdminLayout currentPath={path} onNavigate={navigate}>
          <AdminOrders onNavigate={navigate} />
        </AdminLayout>
      );
    }
    if (path === '/admin/configuracoes') {
      return (
        <AdminLayout currentPath={path} onNavigate={navigate}>
          <AdminSettings />
        </AdminLayout>
      );
    }

    // Public Storefront Layout
    let pageComponent: React.ReactNode;

    if (path === '/') {
      pageComponent = <HomePage onNavigate={navigate} />;
    } else if (path === '/catalogo') {
      const searchParams = new URLSearchParams(window.location.search);
      pageComponent = (
        <CatalogPage
          onNavigate={navigate}
          initialSearch={searchParams.get('search') || ''}
          initialCategory={searchParams.get('category') || ''}
        />
      );
    } else if (path.startsWith('/categoria/')) {
      const slug = path.replace('/categoria/', '');
      pageComponent = <CategoryPage slug={slug} onNavigate={navigate} />;
    } else if (path.startsWith('/produto/')) {
      const slug = path.replace('/produto/', '');
      pageComponent = <ProductPage slug={slug} onNavigate={navigate} />;
    } else if (path === '/carrinho') {
      pageComponent = <CartPage onNavigate={navigate} />;
    } else if (path === '/checkout') {
      pageComponent = <CheckoutPage onNavigate={navigate} />;
    } else if (path.startsWith('/pedido/')) {
      const orderNumber = path.replace('/pedido/', '');
      pageComponent = <OrderConfirmationPage orderNumber={orderNumber} onNavigate={navigate} />;
    } else if (path === '/conta') {
      pageComponent = <AccountPage onNavigate={navigate} />;
    } else if (path === '/login') {
      pageComponent = <LoginPage onNavigate={navigate} />;
    } else if (path.startsWith('/politicas/')) {
      const pageKey = path.replace('/politicas/', '');
      pageComponent = <PolicyPage page={pageKey} onNavigate={navigate} />;
    } else {
      pageComponent = (
        <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
          <h2 className="text-3xl font-serif font-medium text-ink">404 - Página não encontrada</h2>
          <p className="text-xs text-ink-muted">A página solicitada não existe ou mudou de endereço.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
          >
            Voltar ao Início
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col min-h-screen">
        <AnnouncementBar />
        <Navbar currentPath={currentPath} onNavigate={navigate} />
        <main className="flex-1">{pageComponent}</main>
        <CartDrawer onNavigate={navigate} />
        <Footer onNavigate={navigate} />
      </div>
    );
  };

  return renderRoute();
}

export function App() {
  return (
    <ToastProvider>
      <StoreProvider>
        <AuthProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </AuthProvider>
      </StoreProvider>
    </ToastProvider>
  );
}

export default App;
